import { Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { StatsAiStateService } from '../../controller/stats-ai-state.service';
import { StatsAiStatsRequest } from '@solverml/api';

interface OpponentOption {
  id: string;
  name: string;
}

interface MatchOption {
  id: string;
  title: string;
  date: string | null;
}

@Component({
  selector: 'app-stats-ai-filter-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './stats-ai-filter-panel.component.html',
  styleUrls: ['./stats-ai-filter-panel.component.scss']
})
export class StatsAiFilterPanelComponent {
  public state = inject(StatsAiStateService);
  
  public opponentOptions = computed<OpponentOption[]>(() => {
    const teamVideos = this.state.teamVideos();
    if (!teamVideos || !teamVideos.data) return [];

    const uniqueOpponents = new Map<string, string>();
    teamVideos.data.forEach(v => {
      if (v.opponent_team_id && v.opponent_team_name) {
        uniqueOpponents.set(v.opponent_team_id, v.opponent_team_name);
      }
    });

    return Array.from(uniqueOpponents.entries()).map(([id, name]) => ({ id, name }));
  });

  public selectedOpponentId: string | null = null;
  public matchOptions = computed<MatchOption[]>(() => {
    const teamVideos = this.state.teamVideos();
    const selectedIds = this.state.selectedVideoIds();
    if (!teamVideos || !teamVideos.data || selectedIds.length === 0) return [];

    const teamVideosById = new Map<string, MatchOption>();
    teamVideos.data.forEach(v => {
      teamVideosById.set(v.video_id, {
        id: v.video_id,
        title: v.video_title,
        date: v.video_date ?? null
      });
    });

    return selectedIds.map(videoId => {
      const found = teamVideosById.get(videoId);
      return found ?? {
        id: videoId,
        title: `Partido ${videoId.slice(0, 8)}`,
        date: null
      };
    });
  });
  public selectedVideoId: string | null = null;

  constructor() {
    // Si se deselecciona un partido que estaba filtrado, limpiamos filtro y recalculamos stats.
    effect(() => {
      const availableIds = new Set(this.matchOptions().map(m => m.id));
      if (this.selectedVideoId && !availableIds.has(this.selectedVideoId)) {
        this.selectedVideoId = null;
        this.onFilterChange();
      }
    });
  }

  onFilterChange(): void {
    const filters: Partial<StatsAiStatsRequest> = {};
    if (this.selectedOpponentId) {
      filters.opponent_team_id = this.selectedOpponentId;
    }
    if (this.selectedVideoId) {
      filters.video_ids = [this.selectedVideoId];
    }
    
    this.state.loadStats(filters);
  }

  clearFilters(): void {
    this.selectedOpponentId = null;
    this.selectedVideoId = null;
    this.state.loadStats({});
  }
}
